import { useTranslation } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';
import {
  Building, Recycle, Globe, TrendingUp, Layers, ShieldCheck,
  UserCheck, Database, Eye as EyeIcon, Headphones,
} from 'lucide-react';

export default function AboutPage() {
  const { t } = useTranslation('about');

  const features = [
    { icon: Building, title: t('stockVal'), desc: t('stockValDesc') },
    { icon: Recycle, title: t('circular'), desc: t('circularDesc') },
    { icon: Globe, title: t('network'), desc: t('networkDesc') },
    { icon: TrendingUp, title: t('prices'), desc: t('pricesDesc') },
    { icon: Layers, title: t('multiSector'), desc: t('multiSectorDesc') },
    { icon: ShieldCheck, title: t('trust'), desc: t('trustDesc') },
  ];

  const trustFeatures = [
    { icon: UserCheck, title: t('verifiedProfiles'), desc: t('verifiedProfilesDesc') },
    { icon: Database, title: t('dataProtected'), desc: t('dataProtectedDesc') },
    { icon: EyeIcon, title: t('moderatedAds'), desc: t('moderatedAdsDesc') },
    { icon: Headphones, title: t('support'), desc: t('supportDesc') },
  ];

  return (
    <>
      <SEO title="À propos" description="DestockPro est la marketplace B2B de déstockage de matériel professionnel en Belgique. Découvrez notre mission et nos valeurs." path="/a-propos" />
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="about-page">
      {/* Hero */}
      <section className="text-center mb-16">
        <h1 className="font-heading text-4xl font-bold text-neutral-900 mb-4">{t('title')}</h1>
        <p className="text-base text-neutral-500 max-w-2xl mx-auto">
          {t('belgianPlatform')}
        </p>
      </section>

      {/* Mission */}
      <section className="mb-16" data-testid="about-mission">
        <h2 className="font-heading text-2xl font-bold text-neutral-900 mb-3">{t('missionTitle')}</h2>
        <p className="text-base text-neutral-600 leading-relaxed mb-6">{t('missionText')}</p>
      </section>

      {/* Why */}
      <section className="mb-16" data-testid="about-why">
        <h2 className="font-heading text-2xl font-bold text-neutral-900 mb-3">{t('whyTitle')}</h2>
        <p className="text-base text-neutral-600 leading-relaxed mb-8">{t('whyText')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div key={i} className="bg-neutral-50 border rounded-xl p-6 hover:bg-white hover:shadow-sm transition-all">
              <f.icon className="w-6 h-6 text-[#1F3F72] mb-3" />
              <h3 className="font-heading text-sm font-semibold text-neutral-900 mb-1">{f.title}</h3>
              <p className="text-xs text-neutral-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <section className="mb-16" data-testid="about-trust">
        <h2 className="font-heading text-2xl font-bold text-neutral-900 mb-3">{t('trustTitle')}</h2>
        <p className="text-base text-neutral-600 leading-relaxed mb-8">{t('trustText')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {trustFeatures.map((f, i) => (
            <div key={i} className="flex items-start gap-4 p-5 bg-white border rounded-xl">
              <div className="w-10 h-10 rounded-lg bg-[#1F3F72]/10 flex items-center justify-center shrink-0">
                <f.icon className="w-5 h-5 text-[#1F3F72]" />
              </div>
              <div>
                <h3 className="font-heading text-sm font-semibold text-neutral-900 mb-1">{f.title}</h3>
                <p className="text-xs text-neutral-500 leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Belgian note */}
      <section className="bg-[#1F3F72] text-white rounded-2xl p-8 text-center" data-testid="about-belgian">
        <h2 className="font-heading text-xl font-bold mb-2">{t('belgianPlatform')}</h2>
        <p className="text-sm text-blue-200 leading-relaxed max-w-lg mx-auto">{t('belgianDesc')}</p>
      </section>
    </div>
    </>
  );
}